import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import theme from "../../../../styles/theme";
import drawSpaceConfig from "./config/drawSpaceConfig";

import useDrawSpaceData from "./hooks/useDrawSpaceData";
import useDrawSpaceEditor from "./hooks/useDrawSpaceEditor";
import useDrawSpaceView from "./hooks/useDrawSpaceView";

import DrawToolbar from "./components/DrawToolbar";
import DrawViewport from "./components/DrawViewport";
import DrawPropertiesPanel from "./components/DrawPropertiesPanel";
import DrawSiteLevelSidebar from "./components/DrawSiteLevelSidebar";

import { printSpatialView } from "./utils/drawPrint";

import createInterfaceRectangle from "../drawspace25D/business/factory/createInterfaceRectangle";

/* ============================================================================
   HELPERS — COUCHE SPATIALE
============================================================================ */

const geometryToDrawableEntity = ({
  id,
  label,
  geometry,
  paletteKey,
  selectable = false,
  contextMovable = false,
  hasSubsystem = false,
  nativeContext = null,
  kind = "EMPRISE",
}) => {
  if (!geometry) return null;

  if (geometry.kind === "RECT") {
    return {
      id,
      label,
      type: drawSpaceConfig.entityTypes.AREA_RECT,
      geometry: {
        x: geometry.x,
        y: geometry.y,
        width: geometry.width,
        height: geometry.height,
      },
      paletteKey,
      selectable,
      contextMovable,
      hasSubsystem,
      nativeContext,
      kind,
    };
  }

  if (geometry.kind === "POLYLINE") {
    return {
      id,
      label,
      type: drawSpaceConfig.entityTypes.AREA_POLYLINE,
      geometry: {
        points: geometry.points ?? [],
      },
      paletteKey,
      selectable,
      contextMovable,
      hasSubsystem,
      nativeContext,
      kind,
    };
  }

  return null;
};

const structureToPropertiesEntity = (mode, activeOuvrage, activeLevel) => {
  if (mode === "SITE" && activeOuvrage) {
    const geometry = activeOuvrage.footprintGeometry ?? {};

    if (geometry.kind === "RECT") {
      return {
        id: activeOuvrage.id,
        type: "OUVRAGE",
        label: activeOuvrage.label,
        geometry: {
          x: geometry.x ?? "",
          y: geometry.y ?? "",
          width: geometry.width ?? "",
          height: geometry.height ?? "",
        },
        attributes: {
          elevation: "",
        },
      };
    }

    if (geometry.kind === "POLYLINE") {
      return {
        id: activeOuvrage.id,
        type: "OUVRAGE",
        label: activeOuvrage.label,
        geometry: {
          points: geometry.points ?? [],
        },
        attributes: {
          elevation: "",
        },
      };
    }
  }

  if (mode === "OUVRAGE" && activeLevel) {
    const geometry = activeLevel.outlineGeometry ?? {};

    if (geometry.kind === "RECT") {
      return {
        id: activeLevel.id,
        type: "LEVEL",
        label: activeLevel.label,
        geometry: {
          x: geometry.x ?? "",
          y: geometry.y ?? "",
          width: geometry.width ?? "",
          height: geometry.height ?? "",
        },
        attributes: {
          elevation: activeLevel.elevation ?? "",
          height: activeLevel.height ?? "",
        },
      };
    }

    if (geometry.kind === "POLYLINE") {
      return {
        id: activeLevel.id,
        type: "LEVEL",
        label: activeLevel.label,
        geometry: {
          points: geometry.points ?? [],
        },
        attributes: {
          elevation: activeLevel.elevation ?? "",
          height: activeLevel.height ?? "",
        },
      };
    }
  }

  return null;
};

const propertiesEntityToStructurePatch = (mode, entity) => {
  if (!entity) return null;


  const hasRect =
    entity.geometry &&
    "x" in entity.geometry &&
    "y" in entity.geometry &&
    "width" in entity.geometry &&
    "height" in entity.geometry;

  const geometry = hasRect
    ? {
        kind: "RECT",
        x: Number(entity.geometry.x),
        y: Number(entity.geometry.y),
        width: Number(entity.geometry.width),
        height: Number(entity.geometry.height),
      }
    : {
        kind: "POLYLINE",
        points: entity.geometry?.points ?? [],
      };

  if (mode === "SITE") {
    return {
      label: entity.label,
      footprintGeometry: geometry,
    };
  }

  if (mode === "OUVRAGE") {
    return {
      label: entity.label,
      elevation:
        entity.attributes?.elevation === ""
          ? null
          : Number(entity.attributes?.elevation),
      height:
        entity.attributes?.height === ""
          ? null
          : Number(entity.attributes?.height),
      outlineGeometry: geometry,
    };
  }

  return null;
};


const interfaceToPropertiesEntity = (item) => {
  if (!item) return null;

  if (item.InterfaceType === "RECTANGLE") {
    return {
      id: item.InterfaceID,
      type: "INTERFACE_RECTANGLE",
      label: item.InterfaceName ?? "",
      geometry: {
        x: item.GeometryData?.x ?? "",
        y: item.GeometryData?.y ?? "",
        width: item.GeometryData?.width ?? "",
        height: item.GeometryData?.height ?? "",
      },
      attributes: {},
    };
  }

  return null;
};

const makeSpatialEntity = ({
  type,
  geometry,
  creationKind,
  activeMode,
  activeSiteId,
  activeOuvrageId,
  activeLevelId,
  activeSubsystemId,
}) => {
  return {
    id: `${creationKind === "EMPRISE" ? "emprise" : "entity"}_${Date.now()}_${Math.floor(
      Math.random() * 1000
    )}`,
    label: "",
    type,
    geometry,
    kind: creationKind,
    status: "draft",
    nativeContext:
      activeMode === "SITE"
        ? "SITE"
        : activeMode === "OUVRAGE"
        ? "OUVRAGE"
        : "NIVEAU",
    parent: {
      siteId: activeSiteId ?? null,
      ouvrageId: activeOuvrageId ?? null,
      levelId: activeMode === "NIVEAU" ? activeLevelId ?? null : null,
    },
    metrics: {},
    subsystemIds: activeSubsystemId ? [activeSubsystemId] : [],
    componentIds: [],
    alerts: [],
  };
};

const getEntityPaletteKey = (entity) => {
  const isEmprise = entity?.kind === "EMPRISE";
  const parent = entity?.parent ?? {};

  if (isEmprise) {
    if (parent.levelId) return "levelEmprise";
    if (parent.ouvrageId) return "ouvrageEmprise";
    if (parent.siteId) return "siteEmprise";
    return "siteEmprise";
  }

  if (parent.levelId) return "levelEntity";
  if (parent.ouvrageId) return "ouvrageEntity";
  if (parent.siteId) return "siteEntity";

  return "levelEntity";
};

const isEntityVisibleInMode = (entity, activeMode) => {
  if (!entity) return false;

  const nativeContext = entity.nativeContext ?? activeMode;
  const kind = entity.kind ?? "ENTITY";

  if (kind === "ENTITY") {
    return nativeContext === activeMode;
  }

  if (kind === "EMPRISE") {
    if (nativeContext === "SITE") {
      return (
        activeMode === "SITE" ||
        activeMode === "OUVRAGE" ||
        activeMode === "NIVEAU"
      );
    }

    if (nativeContext === "OUVRAGE") {
      return activeMode === "OUVRAGE" || activeMode === "NIVEAU";
    }

    if (nativeContext === "NIVEAU") {
      return activeMode === "NIVEAU";
    }
  }

  return nativeContext === activeMode;
};

const resolveViewportEntity = (entity, activeMode) => {
  const nativeContext = entity.nativeContext ?? activeMode;
  const isNative = nativeContext === activeMode;
  const isMovableEntity = entity.kind !== "EMPRISE";

  return {
    ...entity,
    nativeContext,
    paletteKey: getEntityPaletteKey(entity),
    selectable: isNative,
    contextMovable: isNative && isMovableEntity,
    hasSubsystem:
      Array.isArray(entity.subsystemIds) && entity.subsystemIds.length > 0,
  };
};

/* ============================================================================
   COMPOSANT PRINCIPAL
============================================================================ */

const DrawSpace25D = ({
  projectId,
  assetId = null,

  width = drawSpaceConfig.defaults.width,
  height = drawSpaceConfig.defaults.height,

  assetLabel = drawSpaceConfig.defaults.assetLabel,

  showToolbar = drawSpaceConfig.defaults.showToolbar,
  showPropertiesPanel = drawSpaceConfig.defaults.showPropertiesPanel,
  showGrid = drawSpaceConfig.defaults.showGrid,

  enablePersistence = drawSpaceConfig.defaults.enablePersistence,
  enablePrint = drawSpaceConfig.defaults.enablePrint,

  readOnlyMode = drawSpaceConfig.defaults.readOnlyMode,
  disabled = drawSpaceConfig.defaults.disabled,

  printTitle = drawSpaceConfig.defaults.printTitle,
  printOrientation = drawSpaceConfig.defaults.printOrientation,

  editedEntity = null,

  onChange,
  onSelectionChange,
  onDataLoaded,

  style = {},
  toolbarStyle = {},
  viewportStyle = {},
  panelStyle = {},
}) => {
  console.log("DrawSpace projectId:", projectId);
  const viewportHostRef = useRef(null);

  const [gridSize, setGridSize] = useState(drawSpaceConfig.defaults.gridSize);
  const [snapToGrid, setSnapToGrid] = useState(
    drawSpaceConfig.defaults.defaultSnapToGrid
  );

  const [warningMessage, setWarningMessage] = useState(null);
  const [activeMode, setActiveMode] = useState("SITE");
  const [creationKind, setCreationKind] = useState("ENTITY");

  const [activeSystemId, setActiveSystemId] = useState(null);
  const [activeSubsystemId, setActiveSubsystemId] = useState(null);

  const [visibilityState, setVisibilityState] = useState({
    sites: {},
    ouvrages: {},
    levels: {},
    systems: {},
    subsystems: {},
  });

  const [interfaceObjects, setInterfaceObjects] = useState([]);
  const [selectedInterfaceId, setSelectedInterfaceId] = useState(null);

  const clearInterfaceSelection = () => {setSelectedInterfaceId(null);};


  const systems = useMemo(
    () => [
      {
        id: "building-systems",
        label: "Building Systems",
        subsystems: [
          { id: "foundations", label: "Foundations" },
          { id: "floors", label: "Floors" },
          { id: "facades", label: "Facades" },
          { id: "structures", label: "Structures" },
          { id: "roofs", label: "Roofs" },
          { id: "staircases", label: "Staircases" },
          { id: "ceilings", label: "Ceilings" },
          { id: "partitions", label: "Partitions" },
          { id: "openings", label: "Openings" },
          { id: "floor-coverings", label: "Floor coverings" },
          { id: "wall-coverings", label: "Wall coverings" },
          { id: "exterior-works", label: "Exterior works" },
        ],
      },
      {
        id: "technical-systems",
        label: "Technical Systems",
        subsystems: [
          { id: "pressurised-pipework-systems", label: "Pressurised pipework" },
          { id: "gravity-pipe-networks", label: "Gravity networks" },
          { id: "hvac", label: "HVAC" },
          { id: "electrical", label: "Electrical" },
          { id: "automation-and-control", label: "Automation and control" },
          { id: "storage", label: "Storage" },
          { id: "processes", label: "Processes" },
        ],
      },
      {
        id: "civil-systems",
        label: "Civil Systems",
        subsystems: [
          { id: "tanks", label: "Tanks" },
          { id: "water-towers", label: "Water towers" },
          { id: "drainage-channels", label: "Drainage channels" },
          { id: "solar-parks", label: "Solar parks" },
        ],
      },
    ],
    []
  );







  const {
    sites,
    activeSiteId,
    setActiveSiteId,

    activeOuvrageId,
    setActiveOuvrageId,

    activeLevelId,
    setActiveLevelId,

    activeSite,
    activeOuvrage,
    activeLevel,

    updateEntitiesForActiveSite,
    updateEntitiesForActiveOuvrage,
    updateEntitiesForActiveLevel,

    createEntityOnSite,
    createEntityOnOuvrage,
    createEntityOnLevel,

    createInterfaceObjectOnSite,
    createInterfaceObjectOnOuvrage,
    createInterfaceObjectOnLevel,

    updateOuvrageById,
    updateLevelById,

    createSite,
    deleteSite,
    updateSiteAttributes,

    createOuvrage,
    deleteOuvrage,

    createLevel,
    deleteLevel,

    loading,
  } = useDrawSpaceData({
    projectId,
    assetId,
    enablePersistence,
  });

  const {
    zoom,
    offsetX,
    offsetY,
    zoomIn,
    zoomOut,
    resetView,
    pan,
    zoomAtPoint,
    screenToWorld,
  } = useDrawSpaceView();

  const activeContextEntities = useMemo(() => {
    if (activeMode === "SITE") return activeSite?.entities ?? [];
    if (activeMode === "OUVRAGE") return activeOuvrage?.entities ?? [];
    if (activeMode === "NIVEAU") return activeLevel?.entities ?? [];
    return [];
  }, [activeMode, activeSite, activeOuvrage, activeLevel]);

  const updateEntitiesForCurrentContext = useCallback(
    (contextId, nextEntities) => {
      const safeEntities = Array.isArray(nextEntities) ? nextEntities : [];

      if (contextId === activeLevelId && activeLevelId) {
        updateEntitiesForActiveLevel(safeEntities);
        return;
      }

      if (contextId === activeOuvrageId && activeOuvrageId) {
        updateEntitiesForActiveOuvrage(safeEntities);
        return;
      }

      if (contextId === activeSiteId && activeSiteId) {
        updateEntitiesForActiveSite(safeEntities);
      }
    },
    [
      activeSiteId,
      activeOuvrageId,
      activeLevelId,
      updateEntitiesForActiveSite,
      updateEntitiesForActiveOuvrage,
      updateEntitiesForActiveLevel,
    ]
  );

  const {
    tool,
    setTool,
    selectedId,
    selectEntity,
    clearSelection,
    replaceEntity,
    moveEntityInContext,
    copyEntityInContext,
    deleteEntityInContext,
  } = useDrawSpaceEditor({
    activeContextId:
      activeMode === "SITE"
        ? activeSiteId
        : activeMode === "OUVRAGE"
        ? activeOuvrageId
        : activeLevelId,
    entities: activeContextEntities,
    updateEntities: updateEntitiesForCurrentContext,
  });

  const pushEntityToCurrentContext = (entity) => {
    if (!entity) return;

    if (activeMode === "SITE") {
      createEntityOnSite(entity);
      return;
    }

    if (activeMode === "OUVRAGE") {
      createEntityOnOuvrage(entity);
      return;
    }

    if (activeMode === "NIVEAU") {
      createEntityOnLevel(entity);
    }
  };

  const handleCreateRectangle = ({ x, y, width, height }) => {
    const entity = makeSpatialEntity({
      type: drawSpaceConfig.entityTypes.AREA_RECT,
      geometry: { x, y, width, height },
      creationKind,
      activeMode,
      activeSiteId,
      activeOuvrageId,
      activeLevelId,
      activeSubsystemId,
    });

    pushEntityToCurrentContext(entity);
  };

  const handleCreatePolyline = (points) => {
    const entity = makeSpatialEntity({
      type: drawSpaceConfig.entityTypes.AREA_POLYLINE,
      geometry: { points },
      creationKind,
      activeMode,
      activeSiteId,
      activeOuvrageId,
      activeLevelId,
      activeSubsystemId,
    });

    pushEntityToCurrentContext(entity);
  };

  const handleCreateCircle = ({ cx, cy, r }) => {
    const entity = makeSpatialEntity({
      type: drawSpaceConfig.entityTypes.CIRCLE,
      geometry: { cx, cy, r },
      creationKind,
      activeMode,
      activeSiteId,
      activeOuvrageId,
      activeLevelId,
      activeSubsystemId,
    });

    pushEntityToCurrentContext(entity);
  };

  const handleCreateLine = ({ x1, y1, x2, y2 }) => {
    const entity = makeSpatialEntity({
      type: drawSpaceConfig.entityTypes.LINE,
      geometry: { x1, y1, x2, y2 },
      creationKind,
      activeMode,
      activeSiteId,
      activeOuvrageId,
      activeLevelId,
      activeSubsystemId,
    });

    pushEntityToCurrentContext(entity);
  };

  const handleCreatePoint = ({ x, y, symbolId = null, label = "Point" }) => {
    const entity = makeSpatialEntity({
      type: drawSpaceConfig.entityTypes.POINT,
      geometry: { x, y },
      creationKind,
      activeMode,
      activeSiteId,
      activeOuvrageId,
      activeLevelId,
      activeSubsystemId,
    });

    entity.label = label;
    entity.display = {
      type: drawSpaceConfig.displayTypes.SYMBOL,
      symbolId,
    };

    pushEntityToCurrentContext(entity);
  };

  const handleCreateInterfaceRectangle = ({ x, y, width, height }) => {
  const newObject = createInterfaceRectangle({
    x,
    y,
    width,
    height,
    ProjectCode: projectId ?? null,
    SiteCode: activeSiteId ?? null,
    AssetCode: activeOuvrageId ?? null,
    LevelCode: activeLevelId ?? null,
  });

  console.log("NEW INTERFACE OBJECT", newObject);

  setInterfaceObjects((prev) => {
    const next = [...prev, newObject];
    console.log("INTERFACE OBJECTS", next);
    return next;
  });
};

  const selectInterfaceObject = (id) => {
    setSelectedInterfaceId(id);
  };

  const handleVisibilityChange = ({ type, id, visible }) => {
    setVisibilityState((prev) => {
      if (type === "SITE") {
        return {
          ...prev,
          sites: {
            ...prev.sites,
            [id]: visible,
          },
        };
      }

      if (type === "OUVRAGE") {
        return {
          ...prev,
          ouvrages: {
            ...prev.ouvrages,
            [id]: visible,
          },
        };
      }

      if (type === "LEVEL") {
        return {
          ...prev,
          levels: {
            ...prev.levels,
            [id]: visible,
          },
        };
      }

      if (type === "SYSTEM") {
        return {
          ...prev,
          systems: {
            ...prev.systems,
            [id]: visible,
          },
        };
      }

      if (type === "SUBSYSTEM") {
        return {
          ...prev,
          subsystems: {
            ...prev.subsystems,
            [id]: visible,
          },
        };
      }

      return prev;
    });
  };

  const selectedAreaEntity = useMemo(() => {
    return (
      (activeContextEntities ?? []).find((entity) => entity.id === selectedId) ?? null
    );
  }, [activeContextEntities, selectedId]);

  const selectedInterfaceObject = useMemo(() => {
  return (
    (interfaceObjects ?? []).find(
      (item) => item.InterfaceID === selectedInterfaceId
    ) ?? null
  );
}, [interfaceObjects, selectedInterfaceId]);


  const activeFloor = activeLevel ?? null;

  const propertiesEntity = useMemo(() => {
    if (selectedAreaEntity) {
      return selectedAreaEntity;
    }

    if (selectedInterfaceObject) {
      return interfaceToPropertiesEntity(selectedInterfaceObject);
    }

    return structureToPropertiesEntity(activeMode, activeOuvrage, activeLevel);
  }, [
    selectedAreaEntity,
    selectedInterfaceObject,
    activeMode,
    activeOuvrage,
    activeLevel,
  ]);

  const viewportEntities = useMemo(() => {
    if (!activeSite) return [];

    const contextEntities = activeContextEntities ?? [];

    const isSiteVisible = (siteId) => visibilityState.sites?.[siteId] !== false;
    const isOuvrageVisible = (ouvrageId) =>
      visibilityState.ouvrages?.[ouvrageId] !== false;
    const isLevelVisible = (levelId) => visibilityState.levels?.[levelId] !== false;
    const isSystemVisible = (systemId) => visibilityState.systems?.[systemId] !== false;

    if (!isSiteVisible(activeSite.id)) return [];

    const allowedSubsystemIds = systems
      .filter((system) => isSystemVisible(system.id))
      .flatMap((system) => (system.subsystems ?? []).map((s) => s.id));

    const filterVisibleEntities = (entities = []) => {
      return entities
        .filter((entity) => isEntityVisibleInMode(entity, activeMode))
        .filter((entity) => {
          const subsystemIds = entity.subsystemIds ?? [];

          if (subsystemIds.length === 0) return true;

          return subsystemIds.some(
            (subId) =>
              allowedSubsystemIds.includes(subId) &&
              visibilityState.subsystems?.[subId] !== false
          );
        });
    };

    if (activeMode === "SITE") {
      const siteEntities = filterVisibleEntities(contextEntities).map((entity) =>
        resolveViewportEntity(entity, activeMode)
      );

      const ouvrageGeometries = (activeSite.ouvrages ?? [])
        .filter((ouvrage) => isOuvrageVisible(ouvrage.id))
        .map((ouvrage) =>
          geometryToDrawableEntity({
            id: ouvrage.id,
            label: ouvrage.label,
            geometry: ouvrage.footprintGeometry,
            paletteKey: "ouvrage",
            selectable: false,
            contextMovable: false,
            nativeContext: "OUVRAGE",
            kind: "EMPRISE",
            hasSubsystem:
              Array.isArray(ouvrage.subsystemIds) &&
              ouvrage.subsystemIds.length > 0,
          })
        )
        .filter(Boolean);

      return [...ouvrageGeometries, ...siteEntities];
    }

    if (activeMode === "OUVRAGE") {
      const items = [];

      if (activeOuvrage && isOuvrageVisible(activeOuvrage.id)) {
        if (activeOuvrage.footprintGeometry) {
          items.push(
            geometryToDrawableEntity({
              id: `${activeOuvrage.id}_footprint`,
              label: activeOuvrage.label,
              geometry: activeOuvrage.footprintGeometry,
              paletteKey: "ouvrage",
              selectable: false,
              contextMovable: false,
              nativeContext: "OUVRAGE",
              kind: "EMPRISE",
              hasSubsystem:
                Array.isArray(activeOuvrage.subsystemIds) &&
                activeOuvrage.subsystemIds.length > 0,
            })
          );
        }

        (activeOuvrage.levels ?? [])
          .filter((level) => isLevelVisible(level.id))
          .forEach((level) => {
            if (!level.outlineGeometry) return;

            items.push(
              geometryToDrawableEntity({
                id: level.id,
                label: level.label,
                geometry: level.outlineGeometry,
                paletteKey: "level",
                selectable: false,
                contextMovable: false,
                nativeContext: "NIVEAU",
                kind: "EMPRISE",
                hasSubsystem:
                  Array.isArray(level.subsystemIds) &&
                  level.subsystemIds.length > 0,
              })
            );
          });
      }

      const ouvrageEntities = filterVisibleEntities(contextEntities).map((entity) =>
        resolveViewportEntity(entity, activeMode)
      );

      return [...items.filter(Boolean), ...ouvrageEntities];
    }

    if (activeMode === "NIVEAU") {
      const items = [];

      if (activeOuvrage && isOuvrageVisible(activeOuvrage.id)) {
        if (activeOuvrage.footprintGeometry) {
          items.push(
            geometryToDrawableEntity({
              id: `${activeOuvrage.id}_footprint`,
              label: activeOuvrage.label,
              geometry: activeOuvrage.footprintGeometry,
              paletteKey: "ouvrage",
              selectable: false,
              contextMovable: false,
              nativeContext: "OUVRAGE",
              kind: "EMPRISE",
              hasSubsystem:
                Array.isArray(activeOuvrage.subsystemIds) &&
                activeOuvrage.subsystemIds.length > 0,
            })
          );
        }
      }

      if (activeLevel && isLevelVisible(activeLevel.id) && activeLevel.outlineGeometry) {
        items.push(
          geometryToDrawableEntity({
            id: `${activeLevel.id}_outline`,
            label: activeLevel.label,
            geometry: activeLevel.outlineGeometry,
            paletteKey: "level",
            selectable: false,
            contextMovable: false,
            nativeContext: "NIVEAU",
            kind: "EMPRISE",
            hasSubsystem:
              Array.isArray(activeLevel.subsystemIds) &&
              activeLevel.subsystemIds.length > 0,
          })
        );
      }

      const levelEntities = filterVisibleEntities(contextEntities).map((entity) =>
        resolveViewportEntity(entity, activeMode)
      );

      return [...items.filter(Boolean), ...levelEntities];
    }

    return [];
  }, [
    activeMode,
    activeSite,
    activeOuvrage,
    activeLevel,
    activeContextEntities,
    systems,
    visibilityState,
  ]);

  const viewportSelectedId = selectedId;

  const spatialSummary = useMemo(() => {
    const contextEntities = activeContextEntities ?? [];

    const totals = {
      entities: 0,
      emprises: 0,
    };

    const byGeometryType = {
      RECT: 0,
      POLYLINE: 0,
      CIRCLE: 0,
      LINE: 0,
      ARC: 0,
    };

    const bySubsystem = {};

    let semanticCount = 0;
    let explicitGeometryCount = 0;

    contextEntities.forEach((entity) => {
      if (entity.kind === "EMPRISE") {
        totals.emprises += 1;
      } else {
        totals.entities += 1;
      }

      if ((entity.subsystemIds ?? []).length > 0) {
        semanticCount += 1;
      }

      if (entity.type === drawSpaceConfig.entityTypes.AREA_RECT) {
        byGeometryType.RECT += 1;
        explicitGeometryCount += 1;
      } else if (entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE) {
        byGeometryType.POLYLINE += 1;
        explicitGeometryCount += 1;
      } else if (entity.type === drawSpaceConfig.entityTypes.CIRCLE) {
        byGeometryType.CIRCLE += 1;
        explicitGeometryCount += 1;
      } else if (entity.type === drawSpaceConfig.entityTypes.LINE) {
        byGeometryType.LINE += 1;
        explicitGeometryCount += 1;
      } else if (entity.type === drawSpaceConfig.entityTypes.ARC) {
        byGeometryType.ARC += 1;
        explicitGeometryCount += 1;
      }

      (entity.subsystemIds ?? []).forEach((subId) => {
        bySubsystem[subId] = (bySubsystem[subId] ?? 0) + 1;
      });
    });

    const entityCount = contextEntities.length;
    const semanticCoverage =
      entityCount > 0 ? Math.round((semanticCount / entityCount) * 100) : 0;

    const geometryCoverage =
      entityCount > 0 ? Math.round((explicitGeometryCount / entityCount) * 100) : 0;

    let modelMaturity = "LOW";
    if (semanticCoverage >= 50) modelMaturity = "MEDIUM";
    if (semanticCoverage >= 80 && geometryCoverage >= 80) modelMaturity = "HIGH";

    return {
      activeMode,
      creationKind,
      totals,
      byGeometryType,
      bySubsystem,
      semanticCoverage,
      geometryCoverage,
      modelMaturity,
    };
  }, [activeMode, creationKind, activeContextEntities]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(propertiesEntity ?? null);
    }
  }, [propertiesEntity, onSelectionChange]);

  useEffect(() => {
    if (onChange) {
      onChange({
        projectId,
        assetId,
        sites,
        activeSiteId,
        activeOuvrageId,
        activeLevelId,
        activeSite,
        activeOuvrage,
        activeLevel,
        activeMode,
        spatialSummary,
      });
    }
  }, [
    projectId,
    assetId,
    sites,
    activeSiteId,
    activeOuvrageId,
    activeLevelId,
    activeSite,
    activeOuvrage,
    activeLevel,
    activeMode,
    spatialSummary,
    onChange,
  ]);

  useEffect(() => {
    if (onDataLoaded && !loading) {
      onDataLoaded({
        sites,
        activeSiteId,
        activeOuvrageId,
        activeLevelId,
        activeSite,
        activeOuvrage,
        activeLevel,
        activeMode,
      });
    }
  }, [
    loading,
    sites,
    activeSiteId,
    activeOuvrageId,
    activeLevelId,
    activeSite,
    activeOuvrage,
    activeLevel,
    activeMode,
    onDataLoaded,
  ]);

  useEffect(() => {
    setWarningMessage(null);
  }, [activeSiteId, activeOuvrageId, activeLevelId]);

  useEffect(() => {
    if (activeSiteId && visibilityState.sites[activeSiteId] === false) {
      setActiveOuvrageId(null);
      setActiveLevelId(null);
      clearSelection();
      return;
    }

    if (activeOuvrageId && visibilityState.ouvrages[activeOuvrageId] === false) {
      setActiveLevelId(null);
      clearSelection();
      return;
    }

    if (activeLevelId && visibilityState.levels[activeLevelId] === false) {
      clearSelection();
    }
  }, [
    activeSiteId,
    activeOuvrageId,
    activeLevelId,
    visibilityState,
    setActiveOuvrageId,
    setActiveLevelId,
    clearSelection,
  ]);

  const handleToolChange = (nextTool) => {
    if (disabled || readOnlyMode) return;

    const shouldClearSelection =
      nextTool === drawSpaceConfig.tools.SELECT ||
      nextTool === drawSpaceConfig.tools.MOVE ||
      nextTool === drawSpaceConfig.tools.COPY ||
      nextTool === drawSpaceConfig.tools.AREA_RECT ||
      nextTool === drawSpaceConfig.tools.AREA_POLYLINE ||
      nextTool === drawSpaceConfig.tools.LINE ||
      nextTool === drawSpaceConfig.tools.CIRCLE ||
      nextTool === drawSpaceConfig.tools.ARC ||
      nextTool === drawSpaceConfig.tools.PAN ||
      nextTool === drawSpaceConfig.tools.POINT;

    if (shouldClearSelection) {
      clearSelection();
    }

    setTool(nextTool);
  };

  const handlePrint = () => {
    if (!enablePrint) return;
    if (!viewportHostRef.current) return;

    const svgElement = viewportHostRef.current.querySelector("svg");
    if (!svgElement) return;

    printSpatialView({
      svgElement,
      title: printTitle,
      assetLabel,
      floorLabel: activeFloor?.label,
      elevation: activeFloor?.elevation,
      orientation: printOrientation,
    });
  };

  const handleEntityChange = (updatedEntity) => {
    if (!updatedEntity) return;

    if (selectedAreaEntity) {
      replaceEntity(updatedEntity);
      return;
    }

    const patch = propertiesEntityToStructurePatch(activeMode, updatedEntity);
    if (!patch) return;

    if (activeMode === "SITE" && activeOuvrage) {
      updateOuvrageById(activeOuvrage.id, patch);
    }

    if (activeMode === "OUVRAGE" && activeLevel) {
      updateLevelById(activeLevel.id, patch);
    }
  };

  const blockedState = !projectId;

  const emptyState = useMemo(() => {
    if (loading || !projectId || !activeSite) return true;

    const isSiteVisible = visibilityState.sites[activeSite.id] !== false;
    if (!isSiteVisible) return true;

    return false;
  }, [loading, projectId, activeSite, visibilityState]);

  return (
    <div
      style={{
        width,
        display: "flex",
        flexDirection: "column",
        borderRadius: theme.borders.radius.md,
        overflow: "hidden",
        marginLeft: "-12px",
        paddingBottom: "10px",
        height: "calc(100vh - 225px)",
        ...style,
      }}
    >
      {showToolbar && (
        <DrawToolbar
          tool={tool}
          setTool={handleToolChange}
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          resetView={resetView}
          onPrint={handlePrint}
          enablePrint={enablePrint}
          gridSize={gridSize}
          setGridSize={setGridSize}
          snapToGrid={snapToGrid}
          setSnapToGrid={setSnapToGrid}
          style={toolbarStyle}
        />
      )}

      <div
        style={{
          display: "flex",
          width: "100%",
          flex: 1,
          minHeight: height,
        }}
      >
        <DrawSiteLevelSidebar
          sites={sites}
          activeSiteId={activeSiteId}
          activeOuvrageId={activeOuvrageId}
          activeLevelId={activeLevelId}
          activeEntities={activeContextEntities}
          selectedEntityId={selectedId}
          onSelectEntity={selectEntity}
          activeSystemId={activeSystemId}
          activeSubsystemId={activeSubsystemId}
          activeMode={activeMode}
          setActiveMode={setActiveMode}
          setActiveSiteId={setActiveSiteId}
          setActiveOuvrageId={setActiveOuvrageId}
          setActiveLevelId={setActiveLevelId}
          createSite={createSite}
          deleteSite={deleteSite}
          updateSiteAttributes={updateSiteAttributes}
          createOuvrage={createOuvrage}
          deleteOuvrage={deleteOuvrage}
          updateOuvrageById={updateOuvrageById}
          createLevel={createLevel}
          deleteLevel={deleteLevel}
          updateLevelById={updateLevelById}
          systems={systems}
          setActiveSystemId={setActiveSystemId}
          setActiveSubsystemId={setActiveSubsystemId}
          creationKind={creationKind}
          setCreationKind={setCreationKind}
          onVisibilityChange={handleVisibilityChange}
        />

        <div
          ref={viewportHostRef}
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: "100%",
            height: "100%",
            position: "relative",
            overflow: "hidden",
            border: "1px solid rgba(22, 77, 99, 0.28)",
            borderRadius: theme.borders.radius.md,
            boxShadow: `
              0 0 0 0.5px rgba(22, 77, 99, 0.12),
              0 2px 6px rgba(0,0,0,0.04),
              0 8px 18px rgba(0,0,0,0.06)
            `,
          }}
        >
          {blockedState ? (
            <div
              style={{
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.sm,
                color: theme.colors.text.small,
                background: theme.colors.background.panel,
                ...viewportStyle,
              }}
            >
              No project selected
            </div>
          ) : emptyState ? (
            <div
              style={{
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.sm,
                color: theme.colors.text.small,
                background: theme.colors.background.panel,
                ...viewportStyle,
              }}
            >
              {drawSpaceConfig.labels.empty}
            </div>
          ) : (
            <DrawViewport
              width="100%"
              height={height}
              entities={viewportEntities}
              interfaceObjects={interfaceObjects}
              selectedId={viewportSelectedId}
              selectedInterfaceId={selectedInterfaceId}
              zoom={zoom}
              offsetX={offsetX}
              offsetY={offsetY}
              showGrid={showGrid}
              gridSize={gridSize}
              snapToGrid={snapToGrid}
              screenToWorld={screenToWorld}
              zoomAtPoint={zoomAtPoint}
              tool={tool}
              setTool={setTool}
              selectEntity={(entityId) => {
                const entity =
                  (viewportEntities ?? []).find((e) => e.id === entityId) ?? null;

                if (!entity || entity.selectable === false) return;

                selectEntity(entityId);

                if (activeSubsystemId) {
                  const sourceEntity =
                    (activeContextEntities ?? []).find((e) => e.id === entityId) ?? null;

                  if (!sourceEntity) return;

                  const existing = sourceEntity.subsystemIds ?? [];

                  if (!existing.includes(activeSubsystemId)) {
                    const updated = {
                      ...sourceEntity,
                      subsystemIds: [...existing, activeSubsystemId],
                    };

                    replaceEntity(updated);
                  }
                }
              }}
              selectInterfaceObject={selectInterfaceObject}
              clearSelection={clearSelection}
              clearInterfaceSelection={clearInterfaceSelection}
              createRectangle={handleCreateRectangle}
              createInterfaceRectangle={handleCreateInterfaceRectangle}
              createCircle={handleCreateCircle}
              createLine={handleCreateLine}
              createPolyline={handleCreatePolyline}
              createPoint={handleCreatePoint}
              moveEntityInContext={moveEntityInContext}
              copyEntityInContext={copyEntityInContext}
              deleteEntityInContext={deleteEntityInContext}
              panViewport={pan}
              activeMode={activeMode}
              warningMessage={warningMessage}
              style={viewportStyle}
            />
          )}
        </div>

        {showPropertiesPanel && (
          <DrawPropertiesPanel
            entity={propertiesEntity}
            activeMode={activeMode}
            onChange={handleEntityChange}
            systems={systems}
            style={panelStyle}
          />
        )}
      </div>
    </div>
  );
};

export default DrawSpace25D;